import React from 'react';
import PageTopBar from './PageTopBar';
import SearchBar from './SearchBar';
import Select from './Select';
import Button from './Button';
import Card, { CardBody } from './Card';
import EmptyState from './EmptyState';
import { SkeletonTable } from './Skeleton';
import Pagination from './Pagination';

const FilterIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

interface LibraryListPageProps {
  title: string;
  description: string;
  // Search + tag filter (shared by both libraries)
  search: string;
  onSearchChange: (v: string) => void;
  searchPlaceholder: string;
  tags: { id: string; name: string }[];
  selectedTagId?: string;
  onTagChange: (v: string | undefined) => void;
  // Filter toggle + panel
  showFilters: boolean;
  onToggleFilters: () => void;
  filterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  filterPanel?: React.ReactNode;   // page-specific filter selects
  actions?: React.ReactNode;       // primary actions (Import / Create …)
  // States
  loading: boolean;
  error: string | null;
  onDismissError: () => void;
  isEmpty: boolean;
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: { label: string; onClick: () => void };
  // Pagination
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
  children: React.ReactNode;       // the table / list body
}

/**
 * Shared chrome for the Workflow + Dashboard library list pages: page header,
 * search + tag filter + collapsible filter panel + clear/actions, error banner,
 * loading skeleton / empty state, the table (children), and pagination. Each
 * page supplies its own columns/rows (children), filter selects (filterPanel)
 * and actions; everything else is consistent here.
 */
const LibraryListPage: React.FC<LibraryListPageProps> = ({
  title, description,
  search, onSearchChange, searchPlaceholder,
  tags, selectedTagId, onTagChange,
  showFilters, onToggleFilters, filterCount, hasActiveFilters, onClearFilters,
  filterPanel, actions,
  loading, error, onDismissError,
  isEmpty, emptyTitle, emptyDescription, emptyAction,
  page, totalPages, onPageChange,
  children,
}) => (
  <div className="h-full flex flex-col bg-neutral-50">
    <PageTopBar title={title} description={description} />
    <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <SearchBar value={search} onChange={onSearchChange} placeholder={searchPlaceholder} />

            <Select
              value={selectedTagId ?? ''}
              onChange={e => onTagChange(e.target.value || undefined)}
              options={tags.map(t => ({ value: t.id, label: t.name }))}
              placeholder="All tags"
              className="w-48"
            />

            <button
              onClick={onToggleFilters}
              className={`h-9 px-3 text-sm font-medium border rounded-lg flex items-center gap-2 transition-all duration-fast ${
                showFilters || hasActiveFilters
                  ? 'bg-primary-50 border-primary-200 text-primary-700'
                  : 'bg-surface border-neutral-200 text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300'
              }`}
            >
              <FilterIcon />
              <span>Filters</span>
              {filterCount > 0 && (
                <span className="ml-1 bg-primary-600 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {filterCount}
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={onClearFilters}>
                Clear all
              </Button>
            )}

            {actions && <div className="ml-auto flex items-center gap-3">{actions}</div>}
          </div>

          {showFilters && filterPanel && (
            <Card variant="default" className="animate-fade-in">
              <CardBody className="flex flex-wrap items-end gap-4">{filterPanel}</CardBody>
            </Card>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg flex items-center justify-between animate-fade-in">
            <span className="text-sm text-danger-700">{error}</span>
            <button onClick={onDismissError} className="text-danger-500 hover:text-danger-700 transition-colors">
              <CloseIcon />
            </button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <SkeletonTable rows={6} columns={5} />
        ) : isEmpty ? (
          <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
        ) : (
          children
        )}

        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </div>
  </div>
);

export default LibraryListPage;
